/**
 * Workbench → Support — the application's Support module for the people who
 * build it: the issue board, releases, versioning and the autonomy settings.
 *
 * The SAME shared `SupportWorkspace` the Run view mounts under its "Support"
 * menu entry (`@twasta/page-runtime/support`), bound to the platform's
 * `/api/support` transport — builders who live in the workbench rather than
 * the Run view get the identical screen, not a second one. The open issue
 * rides the URL (`?issue=42`), which is where a platform notification about
 * an issue lands.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SupportWorkspace, supportTabOf } from '@twasta/page-runtime/support'
import { useProjectStore } from '@/store/projectStore'
import { useAuth } from '@/auth/AuthContext'
import { makeSupportRuntime } from '@/api/support'
import { APP_BASE } from '@/config/basePath'
import { platformNotify } from '@/components/runtime-shell'

export default function SupportTab() {
  const currentProject = useProjectStore((s) => s.currentProject)
  const { user } = useAuth()
  const project = currentProject?.application.folder_name || ''
  const runtime = useMemo(() => makeSupportRuntime(project), [project])
  const [params, setParams] = useSearchParams()
  const issue = Number(params.get('issue')) || null
  const tab = supportTabOf(params.get('tab'))

  const onIssueChange = useCallback((n: number | null) => {
    const next = new URLSearchParams(params)
    if (n) next.set('issue', String(n))
    else next.delete('issue')
    setParams(next, { replace: true })
  }, [params, setParams])

  // QC opens the Run view on the fix's staging copy, in its own tab — the
  // same tenant-scoped Run URL the top bar's Run button opens, plus the env.
  const onOpenStaging = useCallback((n: number) => {
    const tenant = user?.tenant_id || '_'
    window.open(
      `${APP_BASE}/run/${encodeURIComponent(tenant)}/${encodeURIComponent(project)}`
        + `?env=${encodeURIComponent(`stage:${n}`)}`,
      '_blank', 'noopener',
    )
  }, [user?.tenant_id, project])

  if (!project) return null
  return (
    <div style={{
      height: 'calc(100vh - 120px)', minHeight: 480, display: 'flex', flexDirection: 'column',
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
      background: 'var(--color-bg)', overflow: 'hidden',
    }}>
      <SupportWorkspace
        key={project}
        runtime={runtime}
        notify={platformNotify}
        onOpenStaging={onOpenStaging}
        initialIssue={issue}
        initialTab={tab}
        onIssueChange={onIssueChange}
        newIssueSource="builder"
        getContext={() => ({ surface: 'builder', screen: 'Workbench' })}
      />
    </div>
  )
}
